import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Table, Badge, Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/Axios';

const PropertyPassedRooms = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingRoomId, setUploadingRoomId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchPropertyAndRooms();
  }, [propertyId]);

  const fetchPropertyAndRooms = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Token ${token}` };

      // Fetch property details
      const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`, { headers });
      setProperty(propertyResponse.data);

      // Fetch all rooms and filter for passed inspections for this property
      const roomsResponse = await axiosInstance.get('/api/rooms/all/', { headers });
      const allRooms = Array.isArray(roomsResponse.data) ? roomsResponse.data : [];
      
      // Filter rooms that belong to this property and have passed inspection
      const passedRooms = allRooms.filter(room => {
        // Check if room belongs to this property via floor -> block -> property
        const belongsToProperty = room.property_id === parseInt(propertyId);
        const inspectionPassed = room.inspection_status === 'inspection_passed';
        return belongsToProperty && inspectionPassed;
      });

      setRooms(passedRooms);
    } catch (err) {
      console.error('Error fetching property and rooms:', err);
      setError('Failed to load property rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (roomId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingRoomId(roomId);
    setError('');
    setSuccessMessage('');

    try {
      const headers = { Authorization: `Token ${token}` };

      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('room', roomId);
        formData.append('image', files[i]);
        formData.append('description', `Inspection image ${i + 1}`);

        await axiosInstance.post('/api/upload-room-image/', formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      setSuccessMessage(`Successfully uploaded ${files.length} image(s) for Room ${roomId}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading images:', err);
      setError('Failed to upload images. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploadingRoomId(null);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">
              <i className="bi bi-door-closed me-2"></i>
              Passed Rooms - {property?.trading_name || `Property #${propertyId}`}
            </h3>
            <Button
              color="secondary"
              size="sm"
              onClick={() => navigate('/inspector-dashboard/passed-properties')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Passed Properties
            </Button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}

          {property && (
            <Card className="mb-3">
              <CardBody>
                <Row>
                  <Col md="6">
                    <p className="mb-1"><strong>Trading Name:</strong> {property.trading_name || 'N/A'}</p>
                    <p className="mb-1"><strong>Address:</strong> {property.addresslines || 'N/A'}</p>
                    <p className="mb-0"><strong>City:</strong> {property.city}, {property.province}</p>
                  </Col>
                  <Col md="6">
                    <p className="mb-1"><strong>Property Status:</strong> <Badge color="success">Inspection Passed</Badge></p>
                    <p className="mb-1"><strong>Inspection Date:</strong> {property.inspection_date ? new Date(property.inspection_date).toLocaleDateString() : 'N/A'}</p>
                    <p className="mb-0"><strong>Total Passed Rooms:</strong> {rooms.length}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                Inspection Passed Rooms ({rooms.length})
              </CardTitle>

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3 mb-0">No passed room inspections found for this property.</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Room ID</th>
                      <th>Room Number</th>
                      <th>Floor</th>
                      <th>Block</th>
                      <th>Beds</th>
                      <th>Gender</th>
                      <th>Status</th>
                      <th>Inspection Date</th>
                      <th>Upload Images</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => (
                      <tr key={room.id}>
                        <td>#{room.id}</td>
                        <td><strong>{room.room_number}</strong></td>
                        <td>{room.floor_name || room.floor_number || 'N/A'}</td>
                        <td>{room.block_name || room.block_number || 'N/A'}</td>
                        <td>{room.no_of_beds || room.num_beds || 'N/A'}</td>
                        <td>
                          {room.room_gender ? (
                            <Badge color={
                              room.room_gender === 'male' ? 'info' : 
                              room.room_gender === 'female' ? 'danger' : 
                              'secondary'
                            }>
                              {room.room_gender.charAt(0).toUpperCase() + room.room_gender.slice(1)}
                            </Badge>
                          ) : 'N/A'}
                        </td>
                        <td>
                          <Badge color="success">
                            <i className="bi bi-check-circle me-1"></i>
                            Passed
                          </Badge>
                        </td>
                        <td>
                          {room.inspection_date 
                            ? new Date(room.inspection_date).toLocaleDateString()
                            : 'N/A'
                          }
                        </td>
                        <td>
                          <div>
                            <Button
                              color="primary"
                              size="sm"
                              disabled={uploadingRoomId === room.id}
                              onClick={() => document.getElementById(`file-upload-${room.id}`).click()}
                            >
                              {uploadingRoomId === room.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1"></span>
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-cloud-upload me-1"></i>
                                  Upload
                                </>
                              )}
                            </Button>
                            <input
                              id={`file-upload-${room.id}`}
                              type="file"
                              multiple
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => handleImageUpload(room.id, e)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PropertyPassedRooms;
